/**
 * Shared add-to-cart feedback + guarded-add helpers (window.iwAtc).
 *
 * Framework-agnostic (vanilla DOM), so every ATC entry point can share one
 * implementation of the button state machine and the in-flight lock. Consumers
 * today: the PDP #add button (snippets/product_linked-options.liquid) and the
 * sticky ATC (assets/product-sticky-atc.js). Loaded early in scripts_head so it
 * is defined before any inline PDP handler runs.
 *
 * API:
 *   iwAtc.setButtonState(button, 'adding' | 'added' | 'reset')
 *     Swaps the button label to Adding…/Added ✓ (spinner + success come from
 *     the .is-adding/.is-added CSS in custom.css), and toggles aria-busy /
 *     aria-disabled — never native `disabled`, so the button stays in the
 *     accessibility tree and screen readers announce the state change. The
 *     original markup (label span, price, arrow) is stored and restored on
 *     reset, so it works for buttons with or without an inner label element.
 *
 *   iwAtc.guardedAdd(button, run) -> Promise
 *     Per-button in-flight lock (a data-iw-adding flag on the element). No-ops
 *     if an add is already running on that button. Sets 'adding', awaits run(),
 *     then 'added' (brief flourish, auto-reset) on success or 'reset' on
 *     failure. Always releases the lock in a finally tied to run()'s real
 *     lifecycle. `run` owns all user-facing messaging; it must reject only when
 *     the item was NOT added (post-add drawer failures should be swallowed
 *     inside run so the button still confirms success).
 *
 *   iwAtc.fetchJsonWithTimeout(url, options, ms) -> Promise<{response, data}>
 *     fetch() + JSON parse under one AbortController timeout (covers the body
 *     read, not just headers), so a hung request can never leave a guarded
 *     button stuck. Defaults to 10s.
 */
(function () {
  if (window.iwAtc) return;

  var ADDING_LABEL = 'Adding…';
  var ADDED_LABEL = 'Added ✓';
  var ADDED_HOLD_MS = 1800;
  var DEFAULT_TIMEOUT_MS = 10000;

  var ADDING_ANNOUNCE = 'Adding to cart';
  var ADDED_ANNOUNCE = 'Added to cart';
  var ERROR_ANNOUNCE = 'Unable to add to cart';

  var liveRegion = null;
  var announceTimer = null;
  function ensureLiveRegion() {
    if (liveRegion) return liveRegion;
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.style.cssText =
      'position:absolute;width:1px;height:1px;margin:-1px;padding:0;' +
      'overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);' +
      'white-space:nowrap;border:0;';
    document.body.appendChild(liveRegion);
    return liveRegion;
  }

  function announce(message) {
    if (!message) return;
    var region = ensureLiveRegion();
    clearTimeout(announceTimer);
    region.textContent = '';
    announceTimer = setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  function labelMarkup(text) {
    var span = document.createElement('span');
    span.className = 'iw-atc-label';
    span.textContent = text;
    return span.outerHTML;
  }

  function setButtonState(button, state) {
    if (!button) return;
    if (state === 'adding') {
      if (button.dataset.iwLabel == null) button.dataset.iwLabel = button.innerHTML;
      button.classList.add('is-adding');
      button.classList.remove('is-added');
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      button.innerHTML = labelMarkup(ADDING_LABEL);
    } else if (state === 'added') {
      button.classList.remove('is-adding');
      button.classList.add('is-added');
      button.setAttribute('aria-busy', 'false');
      button.removeAttribute('aria-disabled');
      button.innerHTML = labelMarkup(ADDED_LABEL);
    } else {
      button.classList.remove('is-adding');
      button.classList.remove('is-added');
      button.setAttribute('aria-busy', 'false');
      button.removeAttribute('aria-disabled');
      if (button.dataset.iwLabel != null) {
        button.innerHTML = button.dataset.iwLabel;
        delete button.dataset.iwLabel;
      }
    }
  }

  var resetTimers = new WeakMap();

  function guardedAdd(button, run) {
    if (button && button.dataset.iwAdding) return Promise.resolve();
    if (button) {
      button.dataset.iwAdding = '1';
      if (resetTimers.has(button)) {
        clearTimeout(resetTimers.get(button));
        resetTimers.delete(button);
      }
    }
    setButtonState(button, 'adding');
    announce(ADDING_ANNOUNCE);
    return Promise.resolve()
      .then(run)
      .then(
        function () {
          setButtonState(button, 'added');
          announce(ADDED_ANNOUNCE);
          if (button) {
            resetTimers.set(
              button,
              setTimeout(function () {
                resetTimers.delete(button);
                setButtonState(button, 'reset');
              }, ADDED_HOLD_MS)
            );
          }
        },
        function (err) {
          console.warn('ATC add failed', err);
          setButtonState(button, 'reset');
          announce(ERROR_ANNOUNCE);
        }
      )
      .finally(function () {
        if (button) delete button.dataset.iwAdding;
      });
  }

  async function fetchJsonWithTimeout(url, options, ms) {
    options = options || {};
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, ms || DEFAULT_TIMEOUT_MS);
    try {
      var response = await fetch(
        url,
        Object.assign({}, options, { signal: controller.signal })
      );
      var data = await response.json();
      return { response: response, data: data };
    } finally {
      clearTimeout(timer);
    }
  }

  window.iwAtc = {
    setButtonState: setButtonState,
    guardedAdd: guardedAdd,
    fetchJsonWithTimeout: fetchJsonWithTimeout,
    announce: announce,
  };
})();
